import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { hideLoading, LoadableDataState, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import React, { Component } from "react";
import { TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import Strings from "@my-dld/assets/strings";
import FormSection from "@my-dld/components/Form/FormSection";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import TextInputField from "@eres/forms-web/TextInputField";
import FormRow from "@eres/forms-web/FormRow";
import CheckBoxField from "@eres/forms-web/CheckBoxField";
import { TCRemarksInfo, TenancyContractResponse } from "@eres/ejari-api/Entity/tenant";
import _ from "lodash";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import TermsAndConditionsPopup from "./TermsAndConditionsPopup";
import { EjariApi } from "@eres/ejari-api";
import { EjariPropertyUsage, Lookup } from "@eres/ejari-api/Entity";
import { TenancyContactAction } from "@eres/ejari-core/constants";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { RenderActions } from "./ContractInfo";
import { updateTenantContext } from "@eres/ejari-core/contexts/TenantContext";
import FormMandatory from "../FormMandatory";

interface Props extends RoutedViewProps {

}

interface State extends LoadableDataState {
	newData?: TCRemarksInfo;
	orgData?: TCRemarksInfo;
	showTerms?: boolean;
}

class RemarksInfo extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
		};
	}

	componentDidMount(): void {
		const { remarksInfo } = this.context;
		this.updateFromContext();
		if ( remarksInfo == undefined || remarksInfo.Terms == undefined ) {
			this.loadTermsAndConditions();
		}
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-12 form-group",
			errorManager: defaultErrorManager,
		};
		const { action } = this.context;
		const { showTerms } = this.state;
		const data = this.getUpdatedData();
		return <div className="tender-registration-form">
			<FormSection title={ Strings.remarks }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<TextInputField
						required={ true }
						id={ "Remarks" }
						label={ Strings.remarks }
						valueManager={ dualDataManager }
						customInput={ <textarea rows={ 4 }/> }/>
				</FormRow>
				<div className="d-flex pb-2">
					<CheckBoxField
						{ ...filedProps }
						id={ "isAgreed" }
						label={ Strings.i_agree_to_the }
						valueManager={ dualDataManager }
						/>
					<a className="btn-link ms-1" onClick={ this.onShowTerms }>{ Strings.terms_and_conditions }</a>
				</div>
			</FormSection>
			<FormRow { ...filedProps }>
				<RenderActions
					action={ action! } onContinue={ this.onSubmit } onSave={ this.onSave }
					onBack={ this.onBack } pLabel={ Strings.submit }/>
			</FormRow>
			{ showTerms && <TermsAndConditionsPopup
				terms={ data.Terms }
				onDismiss={ this.onDismissTerms }/> }
		</div>;
	}

	private updateFromContext() {
		const { contract, remarksInfo, action } = this.context;
		const orgRemarks: TCRemarksInfo = {};
		if ( contract && action === TenancyContactAction.UPDATE_CONTRACT ) {
			orgRemarks.Remarks = contract.Remarks;
			orgRemarks.isAgreed = true;
		}
		const state = {
			orgData: orgRemarks || {},
			newData: remarksInfo || {},
		};
		this.setState( state );
	}

	private loadTermsAndConditions() {
		showLoading();
		EjariApi.getTenantTerms().then( ( resp: Lookup[] ) => {
			hideLoading();
			const newData = this.state.newData!;
			this.setState( {
				newData: { ...newData, Terms: resp },
			} );
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private getUpdatedData = ( state: State = this.state ) => {
		const data: TCRemarksInfo = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};

	private onShowTerms = () => {
		this.setState( { showTerms: true } );
	};

	private onDismissTerms = () => {
		this.setState( { showTerms: false } );
	};

	private isValid() {
		const data = this.getUpdatedData();
		const error: ErrorContainer = {};
		let isValid = checkNonEmpty( data, "Remarks", error );
		if ( data.isAgreed === undefined || !data.isAgreed ) {
			isValid = false;
			error.isAgreed = Strings.required;
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private onSubmit = () => {
		if ( this.isValid() ) {
			const data = this.getUpdatedData();
			updateTenantContractContext( {
				remarksInfo: data,
			} );
			this.getTempContractId();
		}
	};

	private onSave = () => {
		if ( this.isValid() ) {
			let { contract } = this.context;
			const data = this.getUpdatedData();
			contract = {
				...contract,
				Remarks: data.Remarks,
			};
			updateTenantContractContext( { contract: contract, remarksInfo: data, canResubmit: true } );
			this.onBack();
		}
	};

	private onBack = () => {
		this.props.history.goBack();
	};

	private getTempContractId() {
		const { action, tempContractId } = this.context;
		showLoading();
		if ( tempContractId == undefined ) {
			EjariApi.getTemporaryContractId().then( resp => {
				updateTenantContractContext( {
					tempContractId: resp.TempContractId,
				} );
				this.checkAndSubmitContract( action! );
			} ).catch( e => {
				showErrorMessage( e.toString() );
				hideLoading();
			} );
		} else {
			this.checkAndSubmitContract( action! );
		}
	}

	private checkAndSubmitContract( action: TenancyContactAction ) {
		requestAnimationFrame( () => {
			if ( action === TenancyContactAction.NEW_CONTRACT ) {
				this.submitContract();
			} else if ( action === TenancyContactAction.RENEW_CONTRACT ) {
				this.renewContract();
			}
		} );
	}

	private submitContract() {
		trackEventWithData( "TenantContract", "NewTry" );
		this.context.submitContract( this.context ).then( ( resp: TenancyContractResponse ) => {
			trackEventWithData( "TenantContract", "NewSubmitted",
				{ contractId: resp.ContractId + "" } );
			hideLoading();
			this.navigateToSuccess( resp );
		} ).catch( e => {
			trackEventWithData( "TenantContract", "NewFailed" );
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private renewContract() {
		const contractId = this.context.contract?.TenancyContractId + "";
		trackEventWithData( "TenantContract", "RenewTry", { contractId: contractId } );
		this.context.renewContract( this.context ).then( ( resp: TenancyContractResponse ) => {
			trackEventWithData( "TenantContract", "RenewSubmitted",
				{ contractId: resp.ContractId + "" } );
			hideLoading();
			this.navigateToSuccess( resp );
		} ).catch( e => {
			trackEventWithData( "TenantContract", "RenewFailed", { contractId: contractId } );
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private navigateToSuccess( resp: TenancyContractResponse ) {
		const { action, property } = this.context;
		let backStack = 3;
		if ( action === TenancyContactAction.NEW_CONTRACT ) {
			backStack = 6;
		} else if ( action === TenancyContactAction.RENEW_CONTRACT ) {
			backStack = 6;
		}
		if ( property && property.EjariPropertyUsageId === EjariPropertyUsage.INDUSTRIAL ) {
			backStack += 1;
		}
		updateTenantContext( { actionDone: true } );
		updateTenantContractContext( { actionDone: true, contractResp: resp } );
		this.props.history.go( -backStack );
	}
}

export default RemarksInfo;
